import React from "react"

import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EditableTitle,
} from "@coval/ui"
import { Check, Edit2, MoreVertical, RefreshCw, Trash2, X } from "lucide-react"

import { useDetachedSaveFailureToast } from "@/hooks/useDetachedSaveFailureToast"

import type { SavedView } from "./types"

/**
 * Single saved-view row with inline rename and delete-confirm flow.
 * Generic over the config shape — domain-agnostic.
 *
 * Changing filters after loading a view never edits the view. The row's
 * actions menu is the only way to change a saved view: rename it, overwrite
 * it with the current filters (loaded view only, when `onUpdateView` is
 * given), or delete it.
 *
 * Callers that need a "description" sub-line must store it at
 * `view.config.metadata.description`; if the config shape doesn't match,
 * the description is silently omitted.
 */
interface SavedViewItemProps<TConfig> {
  view: SavedView<TConfig>
  loadedViewId: string | null
  editingViewId: string | null
  confirmDeleteId: string | null
  onLoadView: (view: SavedView<TConfig>) => void
  onStartEdit: (viewId: string, name: string) => void
  onCancelEdit: () => void
  /** Saves the trimmed new name. Rejecting keeps the name field open with the error, or raises a toast once the field has closed. */
  onConfirmEdit: (viewId: string, name: string) => void | Promise<void>
  onStartDelete: (viewId: string) => void
  onCancelDelete: () => void
  onConfirmDelete: (viewId: string) => void
  onUpdateView?: (viewId: string) => void
  canUpdateLoadedView?: boolean
  isUpdating?: boolean
  canManage?: boolean
}

/** The open name field. It mounts only while renaming, so a save that fails after it closes is raised as a toast. */
function SavedViewNameEditor({
  name,
  saving,
  onSave,
  onClose,
}: {
  name: string
  saving: boolean
  onSave: (name: string) => void | Promise<void>
  onClose: () => void
}) {
  const notifyDetachedFailure = useDetachedSaveFailureToast({
    title: "Couldn't rename the view",
    fallback: "The name was not saved. Try again.",
  })
  return (
    <EditableTitle
      size="sm"
      trigger="external"
      editing
      onEditingChange={(editing) => {
        if (!editing) {
          onClose()
        }
      }}
      label="View name"
      value={name}
      saving={saving}
      onSave={(next) => notifyDetachedFailure(Promise.resolve(onSave(next)))}
    />
  )
}

export function SavedViewItem<TConfig>({
  view,
  loadedViewId,
  editingViewId,
  confirmDeleteId,
  onLoadView,
  onStartEdit,
  onCancelEdit,
  onConfirmEdit,
  onStartDelete,
  onCancelDelete,
  onConfirmDelete,
  onUpdateView,
  canUpdateLoadedView = true,
  isUpdating = false,
  canManage = true,
}: SavedViewItemProps<TConfig>) {
  const isActive = loadedViewId === view.ulid
  const isEditing = editingViewId === view.ulid
  const isConfirmingDelete = confirmDeleteId === view.ulid
  // The menu holds focus while open and returns it to its trigger as it closes, so Rename opens the name field only after that, from onCloseAutoFocus.
  const isRenamePendingRef = React.useRef(false)

  // Safely extract description from config.metadata if present — works for any
  // config shape that follows the `{ metadata: { description?: string } }` convention.
  const description = (view.config as { metadata?: { description?: string } })
    .metadata?.description

  return (
    <div
      className={cn(
        "flex items-center justify-between px-4 py-3 group/saved-view transition-colors w-full",
        isActive && "bg-surface-default-hover",
      )}
    >
      {isEditing ? (
        <div className="flex-1 min-w-0">
          <SavedViewNameEditor
            name={view.name}
            saving={isUpdating}
            onSave={(name) => onConfirmEdit(view.ulid, name)}
            onClose={onCancelEdit}
          />
          {description && (
            <div className="text-xs text-coval-text-muted truncate">
              {description}
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer text-left"
          onClick={() => onLoadView(view)}
        >
          <span className="flex-1 min-w-0">
            <span
              className={cn(
                "block text-sm font-medium truncate",
                isActive && "text-coval-text",
              )}
            >
              {view.name}
            </span>
            {description && (
              <span className="block text-xs text-coval-text-muted truncate">
                {description}
              </span>
            )}
          </span>
          {isActive && (
            <Check
              aria-label="Active view"
              className="size-4 shrink-0 text-coval-text"
            />
          )}
        </button>
      )}
      <div
        className={cn(
          "flex items-center gap-1 shrink-0 ml-2",
          !isEditing &&
            !isConfirmingDelete &&
            "opacity-0 group-hover/saved-view:opacity-100 group-focus-within/saved-view:opacity-100 has-[[data-state=open]]:opacity-100 transition-opacity",
        )}
      >
        {isEditing ? null : isConfirmingDelete ? (
          <div className="flex items-center gap-1">
            <Button
              icon
              variant="ghost"
              onClick={() => onConfirmDelete(view.ulid)}
              tooltip="Confirm delete"
              className="text-coval-danger hover:text-coval-danger"
            >
              <Check />
            </Button>
            <Button
              icon
              variant="ghost"
              onClick={onCancelDelete}
              tooltip="Cancel delete"
            >
              <X />
            </Button>
          </div>
        ) : canManage ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                icon
                variant="ghost"
                tooltip="View actions"
                aria-label={`Actions for ${view.name}`}
              >
                <MoreVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onCloseAutoFocus={(event) => {
                if (isRenamePendingRef.current) {
                  isRenamePendingRef.current = false
                  event.preventDefault()
                  onStartEdit(view.ulid, view.name)
                }
              }}
            >
              <DropdownMenuItem
                onSelect={() => {
                  isRenamePendingRef.current = true
                }}
              >
                <Edit2 className="size-4" />
                Rename view
              </DropdownMenuItem>
              {isActive && onUpdateView && (
                <DropdownMenuItem
                  disabled={!canUpdateLoadedView || isUpdating}
                  onSelect={() => onUpdateView(view.ulid)}
                >
                  <RefreshCw className="size-4" />
                  Update with current filters
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                destructive
                onSelect={() => onStartDelete(view.ulid)}
              >
                <Trash2 className="size-4" />
                Delete view
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  )
}
